import {
  AdditiveBlending,
  BufferAttribute,
  Color,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  ShaderMaterial,
} from 'three';
import { Palette } from '../Palette.js';
import { GLSL_COMMON } from '../shaders/glslCommon.js';
import { ATLAS_ROWS, GlyphAtlas } from './GlyphAtlas.js';
import {
  DEFAULT_TIMING_TIMELINE,
  stopUniforms,
  type TimingColorTimeline,
} from './TimingPalette.js';

/**
 * Expanding waves drawn as ritual seals. **Still one draw call for all of them.**
 *
 * Two sources share this material, selected by `uChartDriven`:
 *
 * - **dummy** — centre, gate and radius hashed from `uTime` in the vertex shader.
 *   The rendering prototype uses this and its appearance is unchanged.
 * - **chart** — those values arrive as instance attributes written from the
 *   simulation, and the fragment shader adds the seal: an inscribed glyph ring,
 *   a thin rim, radial ticks, and a counter-rotating inner geometry.
 *
 * Every layer of the seal is a distance function evaluated against the same
 * `length(vLocal)` the gameplay ring already uses, and the glyphs are one fetch
 * from a shared atlas. So the decoration costs no geometry, no passes and no
 * draw calls — the entire difference between "a ring" and "a magic circle" is
 * fragment work inside a draw that was already happening.
 *
 * **The decoration is inert.** Gate angle, gate width, radius and thickness come
 * straight from the simulation and are only read. Nothing drawn here feeds back
 * into collision or timing, which is what keeps "見た目と判定を分離" true.
 */

export interface RippleFieldOptions {
  readonly count?: number;
  readonly fieldRadius?: number;
  /** Visual half-thickness of the gameplay ring, in world units. */
  readonly thickness?: number;
  readonly segments?: number;
  readonly seed?: number;
  /**
   * Emissive gain applied to the whole field.
   *
   * Kept as a uniform rather than baked into the instance colours because those
   * colours come straight from the chart (`dangerColor`, `gateColor`), and
   * scaling authored data on the way in would be exactly the kind of silent
   * modification the songpack layer refuses to do.
   */
  readonly gain?: number;
  /** Separate gain for the seal, so it can be dimmed without touching the ring. */
  readonly decorGain?: number;
  /** Separate gain for the inscription, the largest new source of lit pixels. */
  readonly glyphGain?: number;
  /** Start in chart mode instead of the self-animating prototype mode. */
  readonly chartDriven?: boolean;
  /**
   * Judge windows in seconds, and the radius of the tappable centre.
   *
   * Supplied by the runtime from the simulation's own settings rather than
   * defaulted here. The arc's colour and the drawn hit circle are both promises
   * about what the judge will do, and a renderer that invented its own numbers
   * would be making promises nobody has to keep.
   */
  readonly judge?: {
    readonly perfectSeconds: number;
    readonly greatSeconds: number;
    readonly goodSeconds: number;
    readonly noteHitRadius: number;
  };
  /** The approach/overrun palette. Defaults to the one in TimingPalette.ts. */
  readonly timeline?: TimingColorTimeline;
}

/**
 * Flattens the palette into the uniforms both programs read.
 *
 * Built from the same tables the CPU mirror samples, so the tests that assert
 * "blue at spawn, pale gold at the best moment" are asserting about the colours
 * the GPU is actually given.
 */
function timelineUniforms(timeline: TimingColorTimeline): Record<string, { value: unknown }> {
  const approach = stopUniforms(timeline.approach);
  const overrun = stopUniforms(timeline.overrun);
  return {
    uApproachAt: { value: approach.positions },
    uApproachColor: { value: chunkColors(approach.colors) },
    uOverrunAt: { value: overrun.positions },
    uOverrunColor: { value: chunkColors(overrun.colors) },
    uHandoverHeat: { value: timeline.handoverHeat },
    // Capped below 1 so a wave never loses its authored colour completely: the
    // song's own palette stays legible underneath the timing readout.
    uTimelineStrength: { value: 0.82 },
    uMissColor: { value: new Color(...MISS_TIMELINE_COLOR) },
  };
}

function chunkColors(flat: readonly number[]): Color[] {
  const colors: Color[] = [];
  for (let index = 0; index + 2 < flat.length; index += 3) {
    colors.push(new Color(flat[index] as number, flat[index + 1] as number, flat[index + 2] as number));
  }
  return colors;
}

/** Fallbacks used only until the runtime supplies the simulation's real values. */
const FALLBACK_JUDGE = Object.freeze({
  perfectSeconds: 0.06,
  greatSeconds: 0.12,
  goodSeconds: 0.2,
  noteHitRadius: 1.25,
});

/**
 * Timing arc palette, cool-to-warm as the moment approaches.
 *
 * Provisional by intent — the brief asks for something changeable, so they are
 * five named constants in one place rather than literals spread through the
 * shader.
 */
const ARC_COLORS = Object.freeze({
  perfect: [1.0, 0.94, 0.72] as const,
  great: [0.36, 1.0, 0.68] as const,
  good: [0.34, 0.68, 1.0] as const,
  far: [0.42, 0.26, 0.72] as const,
  late: [1.0, 0.3, 0.42] as const,
});

/** Feedback colour per grade, in `TIMING_GRADES` order. */
const GRADE_COLORS: readonly (readonly [number, number, number])[] = [
  [1.0, 0.9, 0.62], // perfect
  [0.4, 1.0, 0.66], // great
  [0.38, 0.72, 1.0], // good
  [0.86, 0.4, 0.9], // bad
  [0.7, 0.24, 0.32], // miss
];

/**
 * How loudly each grade goes out, same order.
 *
 * Measured rather than guessed at: with a flat response, three simultaneous BAD
 * dissolves lit most of the frame, which tells the player their worst taps are
 * the most spectacular thing in the game. The falloff is steep on purpose —
 * PERFECT should be the only one that takes over the screen.
 */
const GRADE_GAINS: readonly number[] = [1.0, 0.62, 0.36, 0.18, 0.1];

/**
 * Where the timing arc lands at the best moment: twelve o'clock.
 *
 * The same angle on every seal, whichever way that seal's arc travels. The
 * direction varies so the field does not look mechanical; the destination must
 * not, or there is nothing for the player to learn.
 */
export const ARC_REFERENCE_ANGLE = Math.PI * 0.5;

/** Pale gold, for the flare at the best moment and for the evaporation. */
const PERFECT_FLASH_COLOR: readonly [number, number, number] = [1.0, 0.95, 0.78];

/** Red-violet, for a failed tap. Close to the BAD grade colour, deliberately. */
const BAD_GLITCH_COLOR: readonly [number, number, number] = [1.0, 0.32, 0.62];

/** Where a missed wave's colour is dragged: desaturated, dark, unmistakable. */
const MISS_TIMELINE_COLOR: readonly [number, number, number] = [0.22, 0.1, 0.26];

/**
 * The inscribed rings, outermost first.
 *
 * Radii are offsets from the wave's own radius in world units, so the whole
 * stack rides the gameplay ring without any part of this table being able to
 * influence where that ring actually is. Heights differ on purpose: an even
 * stack of identical bands reads as a barcode, whereas large / small / large /
 * small reads as a hierarchy.
 *
 * `minRadius` is the level of detail rule. Below it the ring is skipped
 * entirely — on a small wave those bands would be a few pixels tall, which
 * costs the same fill as legible text and looks like grey fuzz.
 */
export interface TextRing {
  readonly name: string;
  /** Inner and outer edge, as offsets from the wave radius, in world units. */
  readonly lo: number;
  readonly hi: number;
  /**
   * Fraction of the band the glyphs themselves fill.
   *
   * Below 1 the text sits inside its band with clear air above and below, which
   * is what separates one ring of writing from the next. Density follows from
   * it for free: the shader sizes a repeat from the *text* height, so a smaller
   * scale means more repetitions of the strip, not stretched letters.
   */
  readonly textScale: number;
  /** Region of the atlas: first row of the family, and how many variants. */
  readonly rowBase: number;
  readonly rowVariants: number;
  readonly gain: number;
  /**
   * Where the ring's ink sits between the wave's two authored colours: 0 is
   * `dangerColor`, 1 is `gateColor`.
   *
   * Both ends come from the chart, so nothing is invented here. Alternating
   * along the stack does two things at once — it gives the seal the warm/cool
   * banding a real mandala has, and it stops twenty-five overlapping waves from
   * summing into one flat wash of whatever hue the chart happened to author.
   */
  readonly colorMix: number;
  /**
   * How far this ring is pulled towards the timeline colour, 0 to 1.
   *
   * Not uniform across the stack on purpose. The outer mantra carries it
   * hardest because it is the largest thing on a seal and the part read in
   * peripheral vision; the ornament rings carry it least, so the figure keeps
   * some depth instead of flattening into a single coloured disc when a dozen
   * of them overlap.
   */
  readonly timelineInfluence: number;
  /** +1 or -1, multiplied by the wave's own seeded direction. */
  readonly direction: 1 | -1;
  /** Rotation rate window, radians per second, before the direction is applied. */
  readonly minSpeed: number;
  readonly maxSpeed: number;
  /** Level of detail: the ring appears only once the wave is this wide. */
  readonly minRadius: number;
  /** Six discrete words addressed by slot, rather than running text. */
  readonly isNote: boolean;
}

/** Must match `MAX_TEXT_RINGS` in the fragment shader. */
export const MAX_TEXT_RINGS = 6;

/**
 * Outermost first. Heights alternate large / small on purpose: an even stack of
 * identical bands reads as a barcode, whereas large-small-large-small reads as
 * a hierarchy, which is what a seal actually looks like.
 *
 * Directions strictly alternate so no two neighbouring rings ever drift
 * together — that opposition is most of what makes the figure look mechanical
 * rather than like one spinning disc.
 */
const TEXT_RINGS: readonly TextRing[] = [
  { name: 'outer mantra', lo: 1.30, hi: 1.76, textScale: 0.92,
    rowBase: ATLAS_ROWS.outer.base, rowVariants: ATLAS_ROWS.outer.variants,
    timelineInfluence: 1, gain: 0.70, colorMix: 0.05, direction: 1, minSpeed: 0.025, maxSpeed: 0.08,
    minRadius: 0.55, isNote: false },
  { name: 'secondary text', lo: 0.90, hi: 1.20, textScale: 0.94,
    rowBase: ATLAS_ROWS.secondary.base, rowVariants: ATLAS_ROWS.secondary.variants,
    timelineInfluence: 0.82, gain: 0.80, colorMix: 0.62, direction: -1, minSpeed: 0.04, maxSpeed: 0.12,
    minRadius: 2.6, isNote: false },
  { name: 'ornament', lo: 0.68, hi: 0.84, textScale: 0.96,
    rowBase: ATLAS_ROWS.ornament.base, rowVariants: ATLAS_ROWS.ornament.variants,
    timelineInfluence: 0.55, gain: 0.55, colorMix: 0.85, direction: 1, minSpeed: 0.03, maxSpeed: 0.09,
    minRadius: 3.4, isNote: false },
  { name: 'note words', lo: 0.10, hi: 0.62, textScale: 1.0,
    rowBase: ATLAS_ROWS.note.base, rowVariants: ATLAS_ROWS.note.variants,
    timelineInfluence: 0.7, gain: 1.0, colorMix: 0.20, direction: -1, minSpeed: 0.03, maxSpeed: 0.15,
    minRadius: 0, isNote: true },
  { name: 'inner mantra', lo: -0.44, hi: -0.14, textScale: 0.94,
    rowBase: ATLAS_ROWS.bija.base, rowVariants: ATLAS_ROWS.bija.variants,
    timelineInfluence: 0.78, gain: 0.84, colorMix: 0.62, direction: 1, minSpeed: 0.02, maxSpeed: 0.10,
    minRadius: 4.2, isNote: false },
  { name: 'inner ornament', lo: -0.74, hi: -0.56, textScale: 0.96,
    rowBase: ATLAS_ROWS.ornament.base, rowVariants: ATLAS_ROWS.ornament.variants,
    timelineInfluence: 0.5, gain: 0.50, colorMix: 0.90, direction: -1, minSpeed: 0.035, maxSpeed: 0.11,
    minRadius: 5.4, isNote: false },
];

/** Exposed so tests and diagnostics can assert the layout rather than guess it. */
export const TEXT_RING_LAYOUT: readonly TextRing[] = TEXT_RINGS;

/** The note ring's index, which is also its slot in the rotation packing. */
export const NOTE_RING_INDEX = TEXT_RINGS.findIndex((ring) => ring.isNote);

/**
 * How many rings a wave of this radius shows.
 *
 * Two conditions, both of which have to hold: the wave has to be wide enough
 * for the ring's text to be worth drawing, and the ring's inner edge has to
 * still be outside the origin — an inward ring on a small wave would fold
 * through the centre and draw itself inside out.
 */
export function ringsVisibleAt(radius: number): number {
  let visible = 0;
  for (const ring of TEXT_RINGS) {
    if (radius >= ring.minRadius && radius + ring.lo >= 0.05) visible += 1;
  }
  return visible;
}

/** One wave's presentation, written per frame by the view binder. */
export interface ChartInstanceData {
  centerX: number;
  centerY: number;
  radius: number;
  gateAngle: number;
  gateHalfWidth: number;
  life: number;
  red: number;
  green: number;
  blue: number;
  gateRed: number;
  gateGreen: number;
  gateBlue: number;
  /**
   * Accumulated rotation of the note ring, in radians.
   *
   * An angle rather than a rate, because the binder derives it from the wave's
   * own age and adds a low-frequency wobble that would not survive being
   * expressed as a constant speed. The gate test for note words reads this same
   * value, so a word's visibility and its drawn position cannot disagree.
   */
  spinOuter: number;
  /** Rotation of the innermost ornament ring and the inner geometry with it. */
  spinInner: number;
  /** Inscription variant, 0..`patternCount`. Selects a row within each family. */
  pattern: number;
  /** 0 = warning, 1 = expanding. */
  state: number;
  /**
   * Which of the ring's words is the note, or a negative number for none.
   *
   * Reserved for the judgement phase: one word on the seal will become the
   * thing the player is scored against, and it has to be picked out of its
   * neighbours before it can be aimed at. Nothing downstream of this field
   * touches collision or timing — it selects a tile and changes how that tile
   * is lit, and that is all it will ever do.
   */
  noteWordIndex: number;
  /** Colour of the note word, independent of `dangerColor`/`gateColor`. */
  noteRed: number;
  noteGreen: number;
  noteBlue: number;
  /** Brightness multiplier applied to the note word alone. */
  noteGain: number;
  /** 0..1 breathing term, added on top of `noteGain` for the note word alone. */
  notePulse: number;
/**
   * Accumulated rotation of the inscribed rings that do not ride `spinOuter`
   * or `spinInner`: outer mantra, secondary text, ornament and inner mantra.
   *
   * Angles rather than rates for the same reason as `spinOuter`: the binder
   * derives them from the wave's own age and adds a wobble that would not
   * survive being expressed as a constant speed.
   */
  spinRing0: number;
  spinRing1: number;
  spinRing2: number;
  spinRing4: number;

  /**
   * `songTime - bestTime`, in seconds. Negative is early.
   *
   * The one number the timing arc is drawn from. The shader turns it into a
   * colour using the same thresholds the simulation grades with, so what the
   * arc says and what the tap scores cannot disagree.
   */
  timingOffset: number;
  /** Accumulated angle of the arc's centre, radians. Reaches 0 at `bestTime`. */
  arcAngle: number;
  /** 0 while playable, rising to 1 across the dissolve. */
  resolveProgress: number;
  /** Index into `TIMING_GRADES`, or -1 while unjudged. */
  gradeCode: number;
  /**
   * Decaying 1→0 after a failed tap, the wave's only feedback for one.
   *
   * A failed tap no longer removes the wave, so without this the input would
   * look ignored — and a player who thinks the game missed their press behaves
   * very differently from one who knows they were early.
   */
  badFlash: number;
  /**
   * Seconds from this wave appearing to its best moment.
   *
   * The shader turns it and `timingOffset` into the convergence progress, so
   * the note reaching the centre and the judge awarding PERFECT are two
   * readings of one clock rather than two animations that agree by luck.
   */
  visualLead: number;
}

const VERTEX = /* glsl */ `
${GLSL_COMMON}

attribute float aAngle;
attribute float aSide;

// Prototype-mode parameters, packed two-deep for the same reason the chart data
// is: this program declares fourteen attributes and the limit is sixteen once
// three's own position/normal/uv are counted. Left as five floats and a vec3
// these cost six slots and the program simply fails to link — silently, as far
// as the picture is concerned, because a field that will not link draws nothing
// at all rather than drawing something wrong.
// A = seed, phase, period, speed
// B = scale, colour.rgb
attribute vec4 aDummyA;
attribute vec4 aDummyB;

// Packed into four vec4s rather than eight separate attributes: the eight
// overran this GPU's 16-attribute limit once the seal needed a second colour,
// and the driver rejected the program outright ("Too many attributes").
// A = centre.xy, radius, life
// B = gateAngle, gateHalfWidth, rotationOuter, rotationInner
// C = dangerColor.rgb, glyphPattern
// D = gateColor.rgb, state
// E = noteColor.rgb, noteWordIndex
// F = noteGain, notePulse, (reserved ×2 for judgement state)
// G = accumulated rotation of rings 0, 1, 2 and 4. Rings 3 and 5 ride B.zw,
//     which the seal already carried, so six inscribed rings cost exactly one
//     new attribute rather than six.
// Eleven of the sixteen slots are in use; F's spare pair is the deliberate
// headroom for the hit/miss flags the judgement phase will need, so that phase
// does not have to repack everything again.
attribute vec4 aChartA;
attribute vec4 aChartB;
attribute vec4 aChartC;
attribute vec4 aChartD;
attribute vec4 aChartE;
attribute vec4 aChartF;
attribute vec4 aChartG;
// H = timingOffset (s), arcAngle, resolveProgress, gradeCode
attribute vec4 aChartH;

uniform float uTime;
uniform float uThickness;
uniform float uFieldRadius;
uniform float uChartDriven;
uniform float uDecorInner;
uniform float uDecorOuter;
/** How far outward an evaporating seal drifts, in world units. */
uniform float uDissolveDrift;

varying vec2  vLocal;
varying float vRadius;
varying float vLife;
varying float vGateAngle;
varying float vGateHalf;
varying vec3  vColor;
varying vec3  vGateColor;
varying vec2  vSpin;
varying float vPattern;
varying float vState;
varying vec3  vNoteColor;
varying float vNoteIndex;
varying vec4  vNoteLevel;
varying vec4  vRingSpin;
varying vec4  vJudge;

void main() {
  vec2  center;
  float radius;
  float life;
  float innerReach;
  float outerReach;

  if (uChartDriven > 0.5) {
    // Everything is decided by the simulation; this stage only places vertices.
    center = aChartA.xy;
    radius = aChartA.z;
    life = aChartA.w;
    vGateAngle = aChartB.x;
    vGateHalf = aChartB.y;
    vSpin = aChartB.zw;
    vColor = aChartC.rgb;
    vPattern = aChartC.w;
    vGateColor = aChartD.rgb;
    vState = aChartD.w;
    vNoteColor = aChartE.rgb;
    vNoteIndex = aChartE.w;
    vNoteLevel = aChartF;
    vRingSpin = aChartG;
    vJudge = aChartH;
    // The seal needs room on both sides of the gameplay ring. Constant in world
    // units rather than proportional to the radius: a proportional band on a
    // fifteen-unit wave would cover half the screen, and twelve of those at once
    // is the difference between comfortable and fill-bound.
    innerReach = uDecorInner;
    outerReach = uDecorOuter;
  } else {
    // Self-animating prototype: cycle index and normalised age from time alone,
    // so ripples keep arriving with no allocation and no bookkeeping.
    float dSeed = aDummyA.x;
    float t = (uTime + aDummyA.y) / aDummyA.z;
    float cycle = floor(t);
    float age = fract(t);

    vec2 place = hash21(dSeed + cycle * 37.13);
    float placeAngle = place.x * TAU;
    // sqrt keeps centres uniform over the disc instead of clustering at the middle.
    float placeRadius = sqrt(place.y) * uFieldRadius * 0.66;
    center = vec2(cos(placeAngle), sin(placeAngle)) * placeRadius;

    vGateAngle = hash11(dSeed + cycle * 11.7 + 3.1) * TAU;
    vGateHalf = mix(0.16, 0.46, hash11(dSeed + cycle * 5.3 + 9.7));
    vColor = aDummyB.yzw;
    vGateColor = aDummyB.yzw;
    vSpin = vec2(0.0);
    vPattern = 0.0;
    vState = 1.0;
    vNoteColor = aDummyB.yzw;
    vNoteIndex = -1.0;
    vNoteLevel = vec4(1.0, 0.0, 0.0, 0.0);
    vRingSpin = vec4(0.0);
    vJudge = vec4(0.0, 0.0, 0.0, -1.0);

    radius = 0.35 + age * aDummyA.z * aDummyA.w * aDummyB.x;
    life = smoothstep(0.0, 0.05, age) * (1.0 - smoothstep(0.68, 1.0, age));
    innerReach = uThickness * 4.0;
    outerReach = uThickness * 4.0;
  }

  // A judged seal evaporates: the figure drifts *outward* and thins, rather
  // than being sucked into its own middle. The band is widened to give that
  // drift somewhere to go — nothing is scaled towards the centre, because an
  // implosion reads as the mandala being consumed and the intent is the
  // opposite, the energy that held it returning to the space around it.
  float judged = step(0.0, vJudge.w);
  float dissolve = clamp(vJudge.z, 0.0, 1.0) * judged;
  outerReach += uDissolveDrift * dissolve;
  innerReach = max(0.0, innerReach - uDissolveDrift * dissolve * 0.25);

  vRadius = radius;
  vLife = life;

  // A dead instance is collapsed to a degenerate point rather than drawn and
  // discarded, which keeps retired pool slots off the rasteriser entirely.
  if (life <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    vLocal = vec2(0.0);
    return;
  }

  float edge = max(radius + mix(-innerReach, outerReach, aSide), 0.002);
  vec2 offset = vec2(cos(aAngle), sin(aAngle)) * edge;

  vLocal = offset;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(center + offset, 0.0, 1.0);
}
`;

const FRAGMENT = /* glsl */ `
${GLSL_COMMON}

#define MAX_TEXT_RINGS 6

uniform sampler2D uGlyphAtlas;
uniform float uGlyphRows;
/** Word slots on the note ring, matching that row's cells. */
uniform float uGlyphWords;
/** Note cell width ÷ row height, so a note word is never stretched. */
uniform float uNoteAspect;
/** Full row width ÷ row height: how much running text one repeat holds. */
uniform float uStripAspect;

// One entry per inscribed ring, outermost first. Uniform arrays rather than
// attributes on purpose — the layout is the same for every wave, so paying for
// it per instance would be waste, and the attribute budget has no room.
uniform float uRingCount;
/** Radial band, as an offset from the wave's own radius, in world units. */
uniform float uRingLo[MAX_TEXT_RINGS];
uniform float uRingHi[MAX_TEXT_RINGS];
uniform float uRingRowBase[MAX_TEXT_RINGS];
uniform float uRingRowVariants[MAX_TEXT_RINGS];
uniform float uRingGain[MAX_TEXT_RINGS];
/** Fraction of the band the glyphs fill; the rest is the air between rings. */
uniform float uRingTextScale[MAX_TEXT_RINGS];
/** 0 draws the ring in dangerColor, 1 in gateColor. Both are authored. */
uniform float uRingColorMix[MAX_TEXT_RINGS];
/** Level of detail: a ring only appears once the wave is this wide. */
uniform float uRingMinRadius[MAX_TEXT_RINGS];
/** 1 for the note ring, which is six discrete words rather than running text. */
uniform float uRingIsNote[MAX_TEXT_RINGS];

// Timing arc. The three windows are the simulation's own judge thresholds, in
// seconds, passed in rather than duplicated so the arc cannot promise a grade
// the judge would not give.
uniform float uJudgePerfect;
uniform float uJudgeGreat;
uniform float uJudgeGood;
uniform vec3  uArcColorPerfect;
uniform vec3  uArcColorGreat;
uniform vec3  uArcColorGood;
uniform vec3  uArcColorFar;
uniform vec3  uArcColorLate;
uniform float uArcGain;
/** Radial band the arc occupies, as offsets from the wave radius. */
uniform float uArcLo;
uniform float uArcHi;
/** Half-width of the arc in radians. */
uniform float uArcHalfWidth;
/** Where the arc lands at the best moment: twelve o'clock, on every wave. */
uniform float uArcReferenceAngle;
/** Pale gold flare across the best moment, and how hard it hits. */
uniform vec3  uPerfectFlashColor;
uniform float uPerfectFlashGain;
/** Outward drift and mote brightness of the evaporation. */
uniform float uDissolveDrift;
uniform float uDissolveDustGain;
/** Red-violet, for the shudder a failed tap leaves behind. */
uniform vec3  uBadGlitchColor;
/** Song-time-driven breathing, shared by every arc so they pulse together. */
uniform float uArcPulse;
/** Per-grade feedback colour, indexed the same way as TIMING_GRADES. */
uniform vec3  uGradeColor[5];
/** How loudly each grade goes out, same order. */
uniform float uGradeGain[5];


#define APPROACH_STOPS 7
#define OVERRUN_STOPS 5

// Handed in from TimingPalette.ts. There are no colour literals in this shader
// on purpose: the palette has one home, and it is not here.
uniform float uApproachAt[APPROACH_STOPS];
uniform vec3  uApproachColor[APPROACH_STOPS];
uniform float uOverrunAt[OVERRUN_STOPS];
uniform vec3  uOverrunColor[OVERRUN_STOPS];
uniform float uHandoverHeat;
/** How far any layer may be pulled off its authored colour, at most. */
uniform float uTimelineStrength;
/** Where a missed wave ends up: desaturated and dark, never a success colour. */
uniform vec3  uMissColor;

/**
 * Walks an ordered stop table.
 *
 * Branchless by construction: a segment already passed contributes t = 1 and
 * overwrites, a future segment contributes t = 0 and does not. So the running
 * mix lands on exactly the right pair without a single conditional.
 */
vec3 sampleApproachStops(float at) {
  vec3 result = uApproachColor[0];
  for (int i = 1; i < APPROACH_STOPS; i += 1) {
    float lo = uApproachAt[i - 1];
    float hi = uApproachAt[i];
    result = mix(result, uApproachColor[i], clamp((at - lo) / max(hi - lo, 1e-5), 0.0, 1.0));
  }
  return result;
}

vec3 sampleOverrunStops(float at) {
  vec3 result = uOverrunColor[0];
  for (int i = 1; i < OVERRUN_STOPS; i += 1) {
    float lo = uOverrunAt[i - 1];
    float hi = uOverrunAt[i];
    result = mix(result, uOverrunColor[i], clamp((at - lo) / max(hi - lo, 1e-5), 0.0, 1.0));
  }
  return result;
}

/**
 * Progress drives the ramp until the handover, then the judgement window does.
 *
 * That switch is what makes the colour mean the same thing at every wave speed:
 * the blue-to-red stretch fills whatever lead the player chose, but orange
 * always means "the GOOD window is open" and pale gold always means PERFECT.
 */
float timelineHeat(float lead, float dt, float goodSeconds) {
  // The outer stretch is normalised to *finish* at the edge of the GOOD window
  // rather than at the best moment. That is the whole trick: the blue-to-orange
  // ramp stretches to fill whatever lead the player chose, but its top end is
  // pinned to a judging threshold, so "orange" means the window is open at 0.5x
  // and at 2x alike.
  float outerSpan = max(lead - goodSeconds, 1e-4);
  float outer = clamp((lead + dt) / outerSpan, 0.0, 1.0);
  float nearness = 1.0 - clamp(abs(dt) / max(goodSeconds, 1e-4), 0.0, 1.0);
  return min(1.0, uHandoverHeat * outer + (1.0 - uHandoverHeat) * nearness);
}

vec3 timelineColorFor(float lead, float dt, float goodSeconds, float gradeCode) {
  vec3 colour = dt > 0.0
    ? sampleOverrunStops(dt)
    : sampleApproachStops(timelineHeat(lead, dt, goodSeconds));
  // A miss falls out of the palette entirely. It has to be impossible to read
  // as any kind of success, including the dark end of a late one.
  float missed = step(3.5, gradeCode);
  return mix(colour, mix(colour, uMissColor, 0.7) * 0.5, missed);
}

/** How strongly each inscribed ring follows the timeline colour. */
uniform float uRingTimeline[MAX_TEXT_RINGS];
/** The gameplay ring and the structural decoration follow it too, less far. */
uniform float uTimelineRing;
uniform float uTimelineDecor;

uniform float uThickness;
uniform float uPulse;
uniform float uGain;
uniform float uDecorGain;
uniform float uGlyphGain;
uniform float uChartDriven;
// Also read by the vertex stage, which builds the band these describe; the
// fragment stage lays the seal out as fractions of the same reach.
uniform float uDecorInner;
uniform float uDecorOuter;

varying vec2  vLocal;
varying float vRadius;
varying float vLife;
varying float vGateAngle;
varying float vGateHalf;
varying vec3  vColor;
varying vec3  vGateColor;
varying vec2  vSpin;
varying float vPattern;
varying float vState;
varying vec3  vNoteColor;
varying float vNoteIndex;
varying vec4  vNoteLevel;
varying vec4  vRingSpin;
varying vec4  vJudge;

/**
 * Colour of the timing arc for an offset from the best moment.
 *
 * Blended rather than stepped. Hard bands would read as four separate states
 * and invite the player to wait for one to appear; a continuous ramp reads as
 * *approaching* something, which is the actual information — the grade is a
 * consequence of when you press, not a thing that happens to the wave.
 *
 * Early and late are not symmetric: late tails to red so an overrun is
 * recognisable at a glance from an approach.
 */
vec3 timingArcColor(
  float offsetSeconds, out float gain, out float width, out float span) {
  float magnitude = abs(offsetSeconds);
  vec3 tint = mix(uArcColorPerfect, uArcColorGreat,
                  smoothstep(uJudgePerfect * 0.5, uJudgeGreat, magnitude));
  tint = mix(tint, uArcColorGood, smoothstep(uJudgeGreat, uJudgeGood, magnitude));
  tint = mix(tint, uArcColorFar, smoothstep(uJudgeGood, uJudgeGood * 3.0, magnitude));
  // Overrun leans red; the approach stays violet.
  tint = mix(tint, uArcColorLate, step(0.0, offsetSeconds)
                                  * smoothstep(uJudgeGood, uJudgeGood * 2.2, magnitude));

  // Brightest and thickest exactly at the best moment, falling away on both
  // sides, so the peak is a moment rather than a plateau.
  float focus = 1.0 - smoothstep(0.0, uJudgeGood * 2.0, magnitude);
  float sharp = 1.0 - smoothstep(0.0, uJudgePerfect * 1.7, magnitude);
  sharp *= sharp;

  gain = mix(0.24, 1.0, focus * focus) * (1.0 + sharp * 2.4);
  gain *= 1.0 + uArcPulse * 0.6 * sharp;
  width = mix(0.5, 1.0, focus) * (1.0 + sharp * 0.75);
  // Length is the cue colour cannot give. A far-off arc is a long smear; as the
  // moment closes it gathers into a short bright bar, and a bar contracting to a
  // point is recognisable in peripheral vision at any hue.
  span = mix(1.0, 0.34, focus) * (1.0 - sharp * 0.4);
  return tint;
}

/** Soft-edged annulus, width given in world units. */
float ringLine(float d, float target, float width, float aa) {
  return 1.0 - smoothstep(width - aa, width + aa, abs(d - target));
}

/**
 * How much of a piece of the seal is still there, part-way through evaporating.
 *
 * The figure is diced into cells along its own angle, and each cell is given a
 * random moment at which it stops existing. So the seal does not wipe from one
 * side or fade uniformly — holes open in several places at once and spread,
 * which is what "coming apart" looks like as opposed to "being turned off".
 *
 * The cell count sets the grain: coarse for structural rings, one cell per
 * cluster for text, so whole syllables drop out and the mantra visibly loses
 * words before it loses letters.
 */
float dissolveMask(float angle, float cells, float layerSeed, float progress) {
  if (progress <= 0.0) return 1.0;
  float cell = floor(angle / TAU * cells + 0.5);
  // Two hashes so neighbouring cells do not vanish in a readable order.
  float when = hash11(cell * 0.7331 + layerSeed);
  when = mix(when, hash11(cell * 3.1719 + layerSeed * 2.13), 0.5);
  // Squeezed into the first 80% so everything is gone before the fade ends,
  // rather than a last stubborn cell popping out at the very end.
  float threshold = when * 0.8;
  return 1.0 - smoothstep(threshold, threshold + 0.22, progress);
}

/**
 * Motes left behind by the evaporation, drifting outward and thinning.
 *
 * The obvious implementation — light up whole hash cells — draws rectangles,
 * because that is what a cell is. Each mote is instead a soft point *inside*
 * its cell, on a fine grid, so what is left of the seal reads as suspended dust
 * rather than as rubble.
 */
float dissolveDust(float angle, float d, float progress) {
  if (progress <= 0.0) return 0.0;
  const float ANGULAR_CELLS = 260.0;
  const float RADIAL_CELLS = 30.0;
  float u = angle * ANGULAR_CELLS;
  float v = d * RADIAL_CELLS;
  float grain = hash11(floor(u) * 1.37 + floor(v) * 91.7);
  if (grain < 0.90) return 0.0;
  // Jittered inside the cell, or the motes sit on a visible lattice.
  vec2 offset = vec2(fract(u), fract(v)) - 0.5
              - (hash21(floor(u) * 3.7 + floor(v) * 5.1) - 0.5) * 0.6;
  float speck = exp(-dot(offset, offset) * 70.0);
  // Brightest in the middle of the dissolve: nothing has come apart yet at the
  // start, and there is nothing left to glow at the end.
  return speck * sin(progress * PI) * 1.6;
}

/**
 * Accumulated rotation of one ring.
 *
 * Rings 3 and 5 ride the two angles the seal already carried, so growing from
 * one inscribed ring to six cost a single new attribute instead of six. The
 * binder gives adjacent rings opposite signs, which is what makes the stack
 * read as a mechanism rather than as one spinning disc.
 */
float ringSpin(int index, vec4 packed, vec2 base) {
  if (index == 0) return packed.x;
  if (index == 1) return packed.y;
  if (index == 2) return packed.z;
  if (index == 3) return base.x;
  if (index == 4) return packed.w;
  return base.y;
}

void main() {
  if (vLife <= 0.0) discard;

  // The evaporation drifts the entire figure outward: sampling at a radius
  // *behind* where the pixel actually is makes the pattern travel away from the
  // centre. One line, and every ring, glyph and rail moves together.
  float judged = step(0.0, vJudge.w);
  float dissolve = clamp(vJudge.z, 0.0, 1.0) * judged;
  float d = length(vLocal) - uDissolveDrift * dissolve;
  float a = atan(vLocal.y, vLocal.x);

  // Screen-space derivative gives a constant-width edge at any zoom, which is
  // what makes an analytic ring look sharper than a tessellated one.
  float aa = fwidth(d) * 1.4 + 1e-5;
  float gateEdge = fwidth(a) * 1.5 + 0.004;
  float delta = angleDelta(a, vGateAngle);
  // 1 outside the gate, 0 inside it.
  float open = smoothstep(vGateHalf - gateEdge, vGateHalf + gateEdge, delta);

  // ---- the gameplay ring, unchanged in meaning -------------------------------
  float band = 1.0 - smoothstep(uThickness - aa, uThickness + aa, abs(d - vRadius));
  float inside = max(vRadius - d, 0.0);
  float wake = exp(-inside * 2.2) * 0.22 * step(d, vRadius);
  float arc = (band + wake) * open;
  // A bright marker on the gate's centre line: the opening has to be findable at
  // a glance, which the requirements make a hard rule rather than a nicety.
  float marker = (1.0 - smoothstep(0.0, gateEdge * 2.5 + 0.02, delta)) * band * 1.6;

  vec3 lit = vColor * (arc + marker);

  if (uChartDriven > 0.5) {
    float reachOut = uDecorOuter;
    float reachIn = uDecorInner;
    float safeRadius = max(vRadius, 0.05);
    float circumference = TAU * safeRadius;

    // Text rings dim towards the gate rather than vanishing. Cutting a wedge out
    // of five concentric inscriptions breaks the circle badly enough that the
    // figure stops reading as a seal; the gameplay ring above still opens
    // completely, which is where the gate has to be legible.
    float textGate = mix(0.20, 1.0, open);

    // The wave's own position on the timeline, from the same two numbers the
    // central note converges on. One source, so the colour of the mandala and
    // the position of the note can never tell the player different things.
    float leadSeconds = max(vNoteLevel.w, 1e-3);
    vec3 timeline = timelineColorFor(leadSeconds, vJudge.x, uJudgeGood, vJudge.w);

    // The gameplay ring carries the timeline too, so a wave reads at a glance
    // even when its inscription is too small to resolve.
    lit = mix(lit, timeline * (arc + marker), uTimelineRing * uTimelineStrength);

    // How close this wave is to its best moment, 0..1. Everything the phase
    // added to make PERFECT legible keys off this one number, so all of those
    // cues peak on the same frame by construction rather than by tuning.
    float perfectGlow = 1.0 - smoothstep(0.0, uJudgePerfect * 1.7, abs(vJudge.x));
    perfectGlow *= perfectGlow;
    // Gone the instant the wave is judged: a dissolving seal must not keep
    // advertising a moment that has already been taken.
    perfectGlow *= 1.0 - judged;

    // ---- the inscribed rings ---------------------------------------------------
    vec3 inscription = vec3(0.0);
    for (int index = 0; index < MAX_TEXT_RINGS; index += 1) {
      if (float(index) >= uRingCount) break;
      // Level of detail. A ring whose text would be a few pixels tall is worse
      // than no ring: it costs the same fill and reads as grey fuzz.
      if (vRadius < uRingMinRadius[index]) continue;

      float lo = vRadius + uRingLo[index];
      float hi = vRadius + uRingHi[index];
      if (lo < 0.05 || d < lo || d > hi) continue;

      float rotated = a + ringSpin(index, vRingSpin, vSpin);
      float rowVariant = mod(vPattern + float(index), uRingRowVariants[index]);
      float row = uRingRowBase[index] + rowVariant;
      float ink = 0.0;
      float isNote = 0.0;
      float gateMask = textGate;

      if (uRingIsNote[index] > 0.5) {
        // Six discrete words, evenly spaced, sized in world units so they stay
        // the same on screen however far the wave has expanded.
        float bandHeight = (hi - lo) * uRingTextScale[index];
        float slotArc = TAU / uGlyphWords;
        float slotOrdinal = floor(rotated / slotArc + 0.5);
        float slotCentre = slotOrdinal * slotArc;
        float slotIndex = mod(slotOrdinal, uGlyphWords);

        // On a young wave the slot is too short in arc length to hold the word,
        // so it scales down — both axes together, because squeezing only the
        // width is what turns script into a smear.
        float fit = clamp(slotArc * safeRadius * 0.88 / (bandHeight * uNoteAspect), 0.0, 1.0);
        isNote = 1.0 - step(0.5, abs(slotIndex - vNoteIndex));
        // The note word is slightly larger as well as brighter: scale is the cue
        // that survives when several waves overlap and colour alone gets muddy.
        float wordHeight = bandHeight * fit * mix(1.0, 1.14, isNote);
        float wordWidth = wordHeight * uNoteAspect;
        float mid = (lo + hi) * 0.5;
        float wordLo = mid - wordHeight * 0.5;

        float u = (rotated - slotCentre) * safeRadius / wordWidth + 0.5;
        float v = (d - wordLo) / max(wordHeight, 1e-4);
        if (u < 0.0 || u > 1.0 || v < 0.0 || v > 1.0) continue;

        ink = texture2D(uGlyphAtlas, vec2(
          (slotIndex + u) / uGlyphWords,
          (row + (1.0 - v)) / uGlyphRows)).a;

        // A word is dropped whole when it lies across the gate; masking per pixel
        // sliced words in half, which reads as damage rather than as an opening.
        // The note word is the deliberate exception — it is the thing the player
        // will have to find, so it is never hidden.
        float wordHalfAngle = wordWidth / (2.0 * safeRadius);
        float slotDelta = angleDelta(slotCentre - vSpin.x, vGateAngle);
        gateMask = max(step(vGateHalf + wordHalfAngle * 0.9, slotDelta), isNote);
      } else {
        // Running text. The repeat count comes from the circumference, so a
        // bigger circle carries *more* text at the same size rather than the
        // same text stretched — that is the whole difference between a ring of
        // writing and a ring of smeared writing.
        float band = (hi - lo) * uRingTextScale[index];
        float repeats = max(2.0, ceil(circumference / (band * uStripAspect)));
        // Ceil rather than round so the recovered height can only shrink, never
        // spill out of the band it was allotted. The aspect stays exact either
        // way: the strip is never scaled unevenly.
        float height = circumference / (repeats * uStripAspect);
        float mid = (lo + hi) * 0.5;
        float textLo = mid - height * 0.5;
        float v = (d - textLo) / height;
        if (v < 0.0 || v > 1.0) continue;

        ink = texture2D(uGlyphAtlas, vec2(
          fract(rotated / TAU * repeats),
          (row + (1.0 - v)) / uGlyphRows)).a;
      }

      // Both ends of this mix come from the chart, so the seal never shows a
      // colour the song did not author; the note word is the one exception,
      // and it is meant to look like one.
      // Whole clusters drop out rather than the ring fading as one piece: the
      // grain is the text's own repeat count, so the mantra loses words before
      // it loses letters.
      float clusterCells = uRingIsNote[index] > 0.5
        ? uGlyphWords
        : max(8.0, ceil(circumference / ((hi - lo) * uRingTextScale[index] * uStripAspect)) * 9.0);
      ink *= dissolveMask(rotated, clusterCells, float(index) * 7.13, dissolve);

      vec3 ringColor = mix(vColor, vGateColor, uRingColorMix[index]);
      // Each ring follows the timeline by its own amount, so the stack still
      // has depth: flattening every layer onto one RGB would trade the mandala
      // for a coloured disc, and the mandala is the game.
      ringColor = mix(ringColor, timeline, uRingTimeline[index] * uTimelineStrength);
      vec3 inkColor = mix(ringColor, vNoteColor, isNote);
      // The whole inscription flares pale gold across the best moment. This is
      // the cue that survives a crowded field: a colour shift is lost among
      // twenty-five seals, but every ring of one wave lighting at once is not.
      inkColor = mix(inkColor, uPerfectFlashColor, perfectGlow * 0.9);
      float gain = uRingGain[index]
        * mix(1.0, vNoteLevel.x * (1.0 + vNoteLevel.y), isNote)
        * (1.0 + perfectGlow * uPerfectFlashGain);
      inscription += inkColor * ink * gain * gateMask;
    }

    // ---- the rails and marks the text is written between -----------------------
    // Hairlines on every ring boundary, so the writing sits between ruled lines
    // instead of floating. Unrolled rather than looped: the boundaries are
    // constants and the compiler folds them.
    float rails = 0.0;
    for (int index = 0; index < MAX_TEXT_RINGS; index += 1) {
      if (float(index) >= uRingCount) break;
      if (vRadius < uRingMinRadius[index]) continue;
      float lo = vRadius + uRingLo[index];
      if (lo < 0.05) continue;
      rails += ringLine(d, lo - 0.035, 0.011, aa)
             + ringLine(d, vRadius + uRingHi[index] + 0.035, 0.011, aa);
    }

    float outerA = a + vRingSpin.x;
    float innerA = a + vSpin.y;

    // Radial ticks around the very outside, their count following the
    // circumference so they never crowd or thin out.
    float tickLo = vRadius + reachOut * 0.90;
    float tickHi = vRadius + reachOut * 1.00;
    float tickCount = max(12.0, floor(circumference * 1.6));
    float tick = step(0.86, abs(cos(outerA * tickCount * 0.5)))
               * step(tickLo, d) * step(d, tickHi);

    // ---- inner geometry, turning the other way -------------------------------
    float innerA1 = ringLine(d, vRadius - reachIn * 0.96, 0.013, aa);
    float innerA2 = ringLine(d, vRadius - reachIn * 0.20, 0.013, aa);
    float innerBand = step(vRadius - reachIn * 0.96, d) * step(d, vRadius - reachIn * 0.20);
    float petal = pow(abs(cos(innerA * 12.0)), 6.0) * innerBand;
    float spokes = step(0.965, abs(cos(innerA * 3.0))) * innerBand;

    // ---- the gate, marked rather than merely absent ---------------------------
    float gateSpan = (1.0 - open) * step(vRadius + reachOut * 0.10, d) * step(d, tickHi);
    float gateRail = (1.0 - open) * (ringLine(d, vRadius + reachOut * 0.12, 0.020, aa)
                                   + ringLine(d, vRadius + reachOut * 0.88, 0.020, aa));

    // Derived from the authored pair rather than invented: the seal reads as one
    // object because every part of it is mixed from the same two colours.
    vec3 innerColor = mix(vColor, vGateColor, 0.45) * 0.85;
    innerColor = mix(innerColor, timeline, uTimelineDecor * uTimelineStrength);
    vec3 railColor = mix(mix(vColor, vGateColor, 0.5), timeline,
                         uTimelineDecor * uTimelineStrength);
    vec3 tickColor = mix(vGateColor, timeline, uTimelineDecor * uTimelineStrength);

    vec3 decor =
        innerColor * (innerA1 + innerA2) * 0.40
      + innerColor * petal * 0.28
      + innerColor * spokes * 0.30
      + railColor * rails * 0.40 * textGate
      + tickColor * tick * 0.40 * textGate
      + mix(vGateColor, timeline, uTimelineDecor * 0.5 * uTimelineStrength)
        * (gateSpan * 0.16 + gateRail * 0.55);

    // ---- the timing arc --------------------------------------------------------
    // Not an input surface. It sits in the same band as the radial ticks so it
    // costs no extra geometry, and the ticks behind it read as the dial the arc
    // is sweeping across.
    float arcGain;
    float arcWidth;
    float arcSpanScale;
    vec3 arcTint = timingArcColor(vJudge.x, arcGain, arcWidth, arcSpanScale);
    float arcLo = vRadius + reachOut * uArcLo;
    float arcHi = vRadius + reachOut * uArcHi;
    float arcMid = (arcLo + arcHi) * 0.5;
    float arcHalfHeight = (arcHi - arcLo) * 0.5 * arcWidth;
    float arcRadial = 1.0 - smoothstep(arcHalfHeight - aa, arcHalfHeight + aa, abs(d - arcMid));
    float arcDelta = angleDelta(a, vJudge.y);
    float arcSpan = uArcHalfWidth * arcSpanScale;
    float arcAngular = 1.0 - smoothstep(arcSpan * 0.55, arcSpan, arcDelta);
    // A short comet tail behind the head, so the direction of travel is legible.
    float trail = exp(-arcDelta * 3.0) * 0.35;
    float arc = arcRadial * (arcAngular + trail) * (1.0 - judged);

    // A fixed notch at twelve o'clock: the place the arc is travelling towards.
    // Without a mark there, "the arc has arrived" is a judgement about an angle
    // the player has to hold in their head. With one it is two things touching.
    float notchDelta = angleDelta(a, uArcReferenceAngle);
    float notch = (1.0 - smoothstep(0.0, uArcHalfWidth * 0.42, notchDelta))
                * (1.0 - smoothstep(arcHalfHeight * 1.5 - aa, arcHalfHeight * 1.5 + aa,
                                    abs(d - arcMid)))
                * (1.0 - judged);

    // Warning is a seal forming: faint, and marked by a point of light at the
    // origin where the wave will open. Expanding is the full figure.
    float stateGain = mix(0.34, 1.0, vState);
    float core = exp(-d * d * 12.0) * mix(1.0, 0.18, vState);

    // Structure comes apart on a coarser grain than the text: rails and ticks
    // are long strokes, so they break into a handful of pieces rather than
    // crumbling.
    decor *= dissolveMask(a, 26.0, 41.7, dissolve);

    lit += decor * uDecorGain * stateGain;
    lit += inscription * uGlyphGain * stateGain;
    lit += vGateColor * core * 0.55;
    lit += arcTint * arc * uArcGain * arcGain * stateGain;
    lit += arcTint * notch * uArcGain * (0.30 + perfectGlow * 1.5);

    // What is left of the seal after it comes apart: motes on their way out.
    lit += mix(vColor, uPerfectFlashColor, 0.6)
         * dissolveDust(a, d, dissolve) * uDissolveDustGain;

    // ---- failed tap: a glitch, and the wave stays -------------------------------
    // Not a dissolve. The seal shudders, tears along a few angular bands and
    // settles again, because the player has to be told the input landed *and*
    // that the wave is still theirs to take.
    if (vNoteLevel.z > 0.0) {
      float g = vNoteLevel.z;
      float tearBand = step(0.55, hash11(floor(a * 7.0 + g * 23.0)));
      lit = mix(lit, lit * 0.25, tearBand * g);
      lit += uBadGlitchColor * g * (0.35 + tearBand * 0.9)
           * (band + rails * 0.4 + inscription.r * 0.5);
    }

    // ---- judged: one flash, then evaporation ------------------------------------
    if (vJudge.w >= 0.0) {
      float p = clamp(vJudge.z, 0.0, 1.0);
      int gradeIndex = int(vJudge.w + 0.5);
      vec3 gradeColor = uGradeColor[0];
      float gradeGain = uGradeGain[0];
      for (int candidate = 1; candidate < 5; candidate += 1) {
        if (candidate == gradeIndex) {
          gradeColor = uGradeColor[candidate];
          gradeGain = uGradeGain[candidate];
        }
      }
      // Front-loaded: the flash has to land on the frame the player pressed,
      // not build up afterwards.
      float flash = exp(-p * 7.0) * gradeGain;
      // A bright point runs once round the circle — the seal signing off. Only
      // the grades worth celebrating get enough gain for it to read.
      float sweepAngle = -PI + p * TAU;
      float sweep = exp(-angleDelta(a, sweepAngle) * 5.0) * (1.0 - p) * gradeGain;
      lit *= 1.0 + flash * 1.9;
      lit += gradeColor * (flash * 0.5 + sweep * 0.85) * uArcGain;
      // Thins as it goes. Combined with the outward drift and the cells falling
      // out, what is left reads as the figure turning into light and leaving
      // rather than as an object being deleted.
      lit *= 1.0 - smoothstep(0.45, 1.0, p);
    }
  }

  float intensity = vLife * (1.0 + uPulse * 0.45) * uGain;
  vec3 color = lit * intensity;
  if (max(color.r, max(color.g, color.b)) <= 0.0016) discard;

  gl_FragColor = vec4(color, 1.0);
}
`;

/**
 * The tappable centre: a small disc at each wave's origin.
 *
 * A second mesh rather than part of the seal, because the seal's geometry is an
 * annulus that follows the expanding radius and simply does not exist near the
 * origin once a wave has grown. It shares the *same* instance attribute
 * objects, so there is still one write path and one upload — the extra cost is
 * a draw call and a disc of about a world unit across per wave.
 *
 * Drawing it at all is a gameplay requirement, not decoration: under this rule
 * the centre is the only thing the player may press, and an invisible target is
 * not a target.
 */
const CORE_VERTEX = /* glsl */ `
${GLSL_COMMON}

attribute vec2 aCorner;

attribute vec4 aChartA;
attribute vec4 aChartC;
attribute vec4 aChartD;
attribute vec4 aChartE;
attribute vec4 aChartF;
attribute vec4 aChartH;

uniform float uHitRadius;
/** How far from the best moment the centre starts to draw in, in seconds. */
uniform float uCorePullWindow;
/** Where the converging note starts, as a multiple of the hit radius. */
uniform float uNoteStartMultiple;

varying vec2  vLocal;
varying float vLife;
varying vec3  vColor;
varying vec3  vNoteColor;
varying vec4  vJudge;
varying float vState;
varying float vPerfect;
varying float vBad;
/** Current radius of the converging note, world units. */
varying float vNoteRadius;
/** The wave's display lead, so the fragment can rebuild its progress. */
varying float vNoteLead;

void main() {
  vLife = aChartA.w;
  vBad = aChartF.z;
  vNoteLead = aChartF.w;
  vColor = aChartC.rgb;
  vNoteColor = aChartE.rgb;
  vJudge = aChartH;
  vState = aChartD.w;

  if (vLife <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    vLocal = vec2(0.0);
    return;
  }

  // ---- the converging note --------------------------------------------------
  // The whole point of the phase. A ring starts wide and closes on the fixed
  // target, arriving exactly at the best moment. Progress comes from the two
  // numbers the simulation already owns — the offset from the best moment and
  // the lead it was given — so the note cannot drift away from the judge.
  //
  //   progress = 1 + timingOffset / visualLead
  //     0 at visualSpawnTime, 1 at bestTime.
  float lead = max(aChartF.w, 1e-3);
  float progress = clamp(1.0 + aChartH.x / lead, 0.0, 1.0);
  // easeOutCubic: most of the travel happens early, and the last stretch is a
  // slow settle onto the target. A linear close arrives without announcing
  // itself; this one is visibly *about to* arrive.
  float eased = 1.0 - pow(1.0 - progress, 3.0);
  vNoteRadius = mix(uHitRadius * uNoteStartMultiple, uHitRadius, eased);

  float offset = aChartH.x;
  // Arrival: a short spring past the target rather than a stop. Only after the
  // moment, so nothing overshoots before it has been earned.
  float sinceBest = max(offset, 0.0);
  float arrival = exp(-sinceBest * 9.0) * step(0.0, offset);
  vNoteRadius *= 1.0 + 0.10 * arrival;
  // Past the moment the note eases back outward, so early and late are told
  // apart by direction of travel and not only by colour.
  vNoteRadius += uHitRadius * 0.22 * smoothstep(0.0, uCorePullWindow, sinceBest);

  vPerfect = 0.0;
  if (aChartH.w < 0.0) {
    float closeness = 1.0 - clamp(abs(offset) / max(uCorePullWindow, 1e-3), 0.0, 1.0);
    vPerfect = closeness * closeness;
  }

  // A judged core swells briefly, then goes with the seal. The quad is sized to
  // whatever the note currently needs and no more — every unit of slack here is
  // transparent pixels blended fifty times a frame at the worst setting.
  float p = clamp(aChartH.z, 0.0, 1.0);
  float scale = max(vNoteRadius, uHitRadius) * 1.42
              * (1.0 + 0.35 * exp(-p * 6.0)) * (1.0 + 0.25 * p);
  vLocal = aCorner * scale;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(aChartA.xy + vLocal, 0.0, 1.0);
}
`;

const CORE_FRAGMENT = /* glsl */ `
${GLSL_COMMON}

uniform float uHitRadius;
uniform float uGain;
uniform float uArcPulse;
uniform vec3  uGradeColor[5];
uniform float uGradeGain[5];
uniform vec3  uPerfectFlashColor;
uniform vec3  uBadGlitchColor;
uniform float uJudgeGood;
/** How strongly the central note itself follows the timeline. */
uniform float uTimelineNote;
/** Brightness of the fixed hit target, independent of the moving note. */
uniform float uHitTargetGain;
/** Minimum ring half-width in world units. */
uniform float uHitTargetWidth;
/** Minimum ring half-width in pixels, so it never falls below one. */
uniform float uHitTargetPixels;
/** How far the target is allowed to follow the timeline, 0..1. */
uniform float uHitTargetTimeline;
/** Extra brightness on the target as the best moment closes in. */
uniform float uHitTargetPerfectLift;

#define APPROACH_STOPS 7
#define OVERRUN_STOPS 5

// Handed in from TimingPalette.ts. There are no colour literals in this shader
// on purpose: the palette has one home, and it is not here.
uniform float uApproachAt[APPROACH_STOPS];
uniform vec3  uApproachColor[APPROACH_STOPS];
uniform float uOverrunAt[OVERRUN_STOPS];
uniform vec3  uOverrunColor[OVERRUN_STOPS];
uniform float uHandoverHeat;
/** How far any layer may be pulled off its authored colour, at most. */
uniform float uTimelineStrength;
/** Where a missed wave ends up: desaturated and dark, never a success colour. */
uniform vec3  uMissColor;

/**
 * Walks an ordered stop table.
 *
 * Branchless by construction: a segment already passed contributes t = 1 and
 * overwrites, a future segment contributes t = 0 and does not. So the running
 * mix lands on exactly the right pair without a single conditional.
 */
vec3 sampleApproachStops(float at) {
  vec3 result = uApproachColor[0];
  for (int i = 1; i < APPROACH_STOPS; i += 1) {
    float lo = uApproachAt[i - 1];
    float hi = uApproachAt[i];
    result = mix(result, uApproachColor[i], clamp((at - lo) / max(hi - lo, 1e-5), 0.0, 1.0));
  }
  return result;
}

vec3 sampleOverrunStops(float at) {
  vec3 result = uOverrunColor[0];
  for (int i = 1; i < OVERRUN_STOPS; i += 1) {
    float lo = uOverrunAt[i - 1];
    float hi = uOverrunAt[i];
    result = mix(result, uOverrunColor[i], clamp((at - lo) / max(hi - lo, 1e-5), 0.0, 1.0));
  }
  return result;
}

/**
 * Progress drives the ramp until the handover, then the judgement window does.
 *
 * That switch is what makes the colour mean the same thing at every wave speed:
 * the blue-to-red stretch fills whatever lead the player chose, but orange
 * always means "the GOOD window is open" and pale gold always means PERFECT.
 */
float timelineHeat(float lead, float dt, float goodSeconds) {
  // The outer stretch is normalised to *finish* at the edge of the GOOD window
  // rather than at the best moment. That is the whole trick: the blue-to-orange
  // ramp stretches to fill whatever lead the player chose, but its top end is
  // pinned to a judging threshold, so "orange" means the window is open at 0.5x
  // and at 2x alike.
  float outerSpan = max(lead - goodSeconds, 1e-4);
  float outer = clamp((lead + dt) / outerSpan, 0.0, 1.0);
  float nearness = 1.0 - clamp(abs(dt) / max(goodSeconds, 1e-4), 0.0, 1.0);
  return min(1.0, uHandoverHeat * outer + (1.0 - uHandoverHeat) * nearness);
}

vec3 timelineColorFor(float lead, float dt, float goodSeconds, float gradeCode) {
  vec3 colour = dt > 0.0
    ? sampleOverrunStops(dt)
    : sampleApproachStops(timelineHeat(lead, dt, goodSeconds));
  // A miss falls out of the palette entirely. It has to be impossible to read
  // as any kind of success, including the dark end of a late one.
  float missed = step(3.5, gradeCode);
  return mix(colour, mix(colour, uMissColor, 0.7) * 0.5, missed);
}

varying vec2  vLocal;
varying float vLife;
varying vec3  vColor;
varying vec3  vNoteColor;
varying vec4  vJudge;
varying float vState;
varying float vPerfect;
varying float vBad;
varying float vNoteRadius;
varying float vNoteLead;

void main() {
  if (vLife <= 0.0) discard;
  float d = length(vLocal);
  float aa = fwidth(d) * 1.4 + 1e-5;

  // The quad has to be as wide as the note is at its widest, but the note is a
  // thin ring and a small disc — nearly all of that quad is empty. Rejecting
  // those pixels before any shading or blending is what keeps a note that
  // starts two and a half hit radii out from costing what its bounding box
  // suggests. With this in place the whole core pass measures at -0.2 ms
  // against a frame without it at the hard 0.5x peak: below the noise floor,
  // i.e. free.
  float ringBand = 0.09 + uHitRadius * 0.08;
  if (d > uHitRadius * 1.2 && abs(d - vNoteRadius) > ringBand) discard;

  // The target: this circle, and nothing outside it, is what a tap reads. Its
  // radius is the simulation's own noteHitRadius, passed in rather than guessed,
  // so the drawn target and the judged target are one circle.
  //
  // Its width is set in **pixels**, not world units. That is the whole repair:
  // the ring used to be 0.014 world units wide, which at this camera distance is
  // under half a pixel, so antialiasing spread it across a fraction of one pixel
  // and there was nothing left to see. A target the player has to aim at cannot
  // be sub-pixel — and it must stay the same apparent weight whatever the camera
  // is doing, which a world-space width does not.
  float pixel = fwidth(d);
  float halfWidth = max(uHitTargetWidth, pixel * uHitTargetPixels);
  float target = 1.0 - smoothstep(halfWidth - aa, halfWidth + aa, abs(d - uHitRadius));
  // A soft fill so the target reads as an *area* to press rather than an
  // outline, and a slightly brighter lip just inside the ring so the boundary
  // survives a busy mandala behind it.
  float fill = (1.0 - smoothstep(uHitRadius * 0.35, uHitRadius, d)) * 0.20;
  float lip = (1.0 - smoothstep(uHitRadius * 0.80, uHitRadius, d)) * 0.16;
  float bead = exp(-d * d * 26.0) * 0.5;

  // The converging note. Thicker than the target ring so the moving thing reads
  // as the subject and the fixed thing as the mark it is closing on.
  // The note is the clearest single reading of the timeline on screen, so it
  // follows the palette hardest of anything.
  float leadSeconds = max(vNoteLead, 1e-3);
  vec3 timeline = timelineColorFor(leadSeconds, vJudge.x, uJudgeGood, vJudge.w);
  vec3 noteColour = mix(vColor, timeline, uTimelineNote * uTimelineStrength);
  // The target follows the timeline only part of the way. It is a landmark, not
  // a readout: the note says *when*, and this says *where*. Letting it go all
  // the way to blue early in the approach is what made a ring that is drawn
  // every frame still impossible to find — blue carries a twelfth of green's
  // luminance, so an early target went dark exactly when the player most needs
  // to know where to aim.
  vec3 targetColour = mix(vColor, timeline, uTimelineNote * uTimelineStrength * uHitTargetTimeline);

  float noteWidth = 0.030 + 0.020 * vPerfect;
  float note = 1.0 - smoothstep(noteWidth - aa, noteWidth + aa, abs(d - vNoteRadius));
  // Four short ticks riding the note, so its rotation-free approach still reads
  // as motion when several notes overlap.
  float spoke = step(0.86, abs(cos(atan(vLocal.y, vLocal.x) * 2.0)));
  note += (1.0 - smoothstep(noteWidth * 2.6 - aa, noteWidth * 2.6 + aa,
                            abs(d - vNoteRadius))) * spoke * 0.5;

  // Breathing keeps the untapped centres alive; it is song-time driven, so it
  // is the same breath across every wave on screen.
  float breathe = 1.0 + uArcPulse * 0.25;

  // The target has its own gain. Sharing one with the moving note is what let a
  // change aimed at the note quietly halve the thing the player aims at.
  //
  // It also lifts as the moment approaches. The two marks are saying different
  // things and the difference has to stay readable: the note is *travelling*
  // and carries the timing, the target is *fixed* and carries the place. So the
  // target sits a little under the note at rest and rises to meet it, rather
  // than being equal (nothing to read) or static (nothing to anticipate).
  float targetLevel = target * uHitTargetGain * (1.0 + uHitTargetPerfectLift * vPerfect);
  vec3 lit = (
      noteColour * note * 0.75
    + targetColour * (targetLevel + lip + fill)
    + vNoteColor * bead
  ) * breathe;

  if (vJudge.w >= 0.0) {
    float p = clamp(vJudge.z, 0.0, 1.0);
    int gradeIndex = int(vJudge.w + 0.5);
    vec3 gradeColor = uGradeColor[0];
    for (int candidate = 1; candidate < 5; candidate += 1) {
      if (candidate == gradeIndex) gradeColor = uGradeColor[candidate];
    }
    float gradeGain = uGradeGain[0];
    for (int candidate = 1; candidate < 5; candidate += 1) {
      if (candidate == gradeIndex) gradeGain = uGradeGain[candidate];
    }
    float flash = exp(-p * 6.0) * gradeGain;
    // A ring races outward from the centre as the wave is absorbed.
    float shell = exp(-abs(d - p * uHitRadius * 2.2) * 7.0) * (1.0 - p) * gradeGain;
    lit += gradeColor * (flash * (bead * 3.0 + fill * 2.0) + shell * 0.8);
    lit *= 1.0 - smoothstep(0.4, 1.0, p);
  } else {
    // Unjudged cores dim during the warning window, matching the seal.
    lit *= mix(0.55, 1.0, vState);
    // Arrival. The note goes pale gold exactly as it lands, which is the same
    // instant the arc reaches twelve o'clock and the inscription flares — all
    // four are this one wave's offset from this one best moment.
    //
    // The target is in this term too, and that is the point of the whole
    // approach: at the best moment the note's radius *is* the hit radius, so the
    // two marks are the same circle and both go pale gold together. The
    // coincidence is the cue. Before it they are two rings closing; at it they
    // are one.
    lit += uPerfectFlashColor * vPerfect
         * (note * 1.6 + target * uHitTargetGain * 0.7 + bead * 1.5 + fill * 1.2);
    // A failed tap shakes the note sideways rather than removing it, and the
    // shake decays without touching the convergence: the note snaps back to
    // wherever the clock says it should be.
    if (vBad > 0.0) {
      float jitter = step(0.5, hash11(floor(d * 40.0) + vBad * 31.0));
      lit = mix(lit, uBadGlitchColor * (note + bead), vBad * (0.45 + jitter * 0.5));
    }
  }

  vec3 color = lit * vLife * uGain;
  if (max(color.r, max(color.g, color.b)) <= 0.0016) discard;
  gl_FragColor = vec4(color, 1.0);
}
`;

export class RippleField {
  readonly object: Mesh;
  /** The tappable centre discs. Added to the scene alongside `object`. */
  readonly coreObject: Mesh;
  /** Number of instance slots, i.e. the most ripples that can be shown at once. */
  readonly capacity: number;
  readonly glyphAtlas: GlyphAtlas;

  private readonly material: ShaderMaterial;
  private readonly geometry: InstancedBufferGeometry;
  private readonly coreMaterial: ShaderMaterial;
  private readonly coreGeometry: InstancedBufferGeometry;

  private readonly chartA: Float32Array;
  private readonly chartB: Float32Array;
  private readonly chartC: Float32Array;
  private readonly chartD: Float32Array;
  private readonly chartE: Float32Array;
  private readonly chartF: Float32Array;
  private readonly chartG: Float32Array;
  private readonly chartH: Float32Array;
  private readonly chartAttributes: InstancedBufferAttribute[] = [];

  constructor(options: RippleFieldOptions = {}) {
    const count = options.count ?? 40;
    const fieldRadius = options.fieldRadius ?? 9;
    const thickness = options.thickness ?? 0.075;
    const segments = options.segments ?? 192;
    const random = mulberry32(options.seed ?? 0x9c3b);
    const judge = options.judge ?? FALLBACK_JUDGE;
    const timeline = options.timeline ?? DEFAULT_TIMING_TIMELINE;
    this.capacity = count;

    this.glyphAtlas = new GlyphAtlas();

    this.geometry = buildAnnulusStrip(segments);
    this.geometry.instanceCount = count;

    // Packed to match the shader: A = seed, phase, period, speed; B = scale, rgb.
    const dummyA = new Float32Array(count * 4);
    const dummyB = new Float32Array(count * 4);
    const tint = new Color();

    // A base period in the region of a slow musical bar. Instances take small
    // integer-ish multiples so bursts feel related without ever lining up.
    const basePeriod = 6.4;

    for (let index = 0; index < count; index += 1) {
      const base = index * 4;
      dummyA[base] = index * 17.31 + 0.5;
      // Spreading phase across the whole period is what keeps ripple births
      // continuous instead of arriving in a visible clump.
      dummyA[base + 1] = random() * basePeriod * 3;
      dummyA[base + 2] = basePeriod * (0.75 + Math.floor(random() * 3) * 0.25);
      dummyA[base + 3] = 0.2 + random() * 0.06;
      dummyB[base] = 0.85 + random() * 0.4;

      const role = random();
      if (role < 0.55) tint.copy(Palette.rippleSafe);
      else if (role < 0.82) tint.copy(Palette.rippleDrift);
      else tint.copy(Palette.rippleDanger);
      tint.offsetHSL((random() - 0.5) * 0.04, 0, (random() - 0.5) * 0.1);

      dummyB[base + 1] = tint.r;
      dummyB[base + 2] = tint.g;
      dummyB[base + 3] = tint.b;
    }

    this.geometry.setAttribute('aDummyA', new InstancedBufferAttribute(dummyA, 4));
    this.geometry.setAttribute('aDummyB', new InstancedBufferAttribute(dummyB, 4));

    // Chart-driven storage. Allocated whichever mode is active so switching at
    // runtime needs no reallocation; `aChartLife` of zero hides a slot.
    this.chartA = new Float32Array(count * 4);
    this.chartB = new Float32Array(count * 4);
    this.chartC = new Float32Array(count * 4);
    this.chartD = new Float32Array(count * 4);
    this.chartE = new Float32Array(count * 4);
    this.chartF = new Float32Array(count * 4);
    this.chartG = new Float32Array(count * 4);
    this.chartH = new Float32Array(count * 4);

    // The core mesh reuses these attribute objects rather than copies of them,
    // so both draws read one buffer written once per frame.
    const shared = new Map<string, InstancedBufferAttribute>();
    const bind = (name: string, data: Float32Array): void => {
      const attribute = new InstancedBufferAttribute(data, 4);
      this.geometry.setAttribute(name, attribute);
      this.chartAttributes.push(attribute);
      shared.set(name, attribute);
    };
    bind('aChartA', this.chartA);
    bind('aChartB', this.chartB);
    bind('aChartC', this.chartC);
    bind('aChartD', this.chartD);
    bind('aChartE', this.chartE);
    bind('aChartF', this.chartF);
    bind('aChartG', this.chartG);
    bind('aChartH', this.chartH);

    this.coreGeometry = buildQuad();
    this.coreGeometry.instanceCount = count;
    for (const name of ['aChartA', 'aChartC', 'aChartD', 'aChartE', 'aChartF', 'aChartH'] as const) {
      this.coreGeometry.setAttribute(name, shared.get(name) as InstancedBufferAttribute);
    }

    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uThickness: { value: thickness },
        uFieldRadius: { value: fieldRadius },
        uPulse: { value: 0 },
        // Forty overlapping ripples each carrying a wake add up fast; at unity
        // gain they were the single brightest contributor in the frame.
        uGain: { value: options.gain ?? 0.42 },
        // Rails, ticks and the inner geometry. Present enough that the writing
        // looks written *on* something, quiet enough not to compete with it.
        uDecorGain: { value: options.decorGain ?? 0.30 },
        // The single gain in front of the inscription; the per-ring weights in
        // `TEXT_RINGS` scale it. Lowered from 1.05 when the seal went from one
        // ring of text to six: the same brightness across four times the ink
        // put the corner lift over budget. Chosen off a measured curve rather
        // than by eye — at twenty-one concurrent waves the corner lift over an
        // uninscribed seal runs 0.0083 at 0.45, 0.0108 at 0.62 and 0.0148 at
        // 0.90, so this is the brightest setting that still clears +0.010.
        uGlyphGain: { value: options.glyphGain ?? 0.56 },
        uChartDriven: { value: options.chartDriven === true ? 1 : 0 },
        uGlyphAtlas: { value: this.glyphAtlas.texture },
        uGlyphRows: { value: this.glyphAtlas.rows },
        uGlyphWords: { value: this.glyphAtlas.noteWords },
        uNoteAspect: { value: this.glyphAtlas.noteAspect },
        uStripAspect: { value: this.glyphAtlas.stripAspect },
        uRingCount: { value: TEXT_RINGS.length },
        uRingLo: { value: TEXT_RINGS.map((ring) => ring.lo) },
        uRingHi: { value: TEXT_RINGS.map((ring) => ring.hi) },
        uRingRowBase: { value: TEXT_RINGS.map((ring) => ring.rowBase) },
        uRingRowVariants: { value: TEXT_RINGS.map((ring) => ring.rowVariants) },
        uRingGain: { value: TEXT_RINGS.map((ring) => ring.gain) },
        uRingTextScale: { value: TEXT_RINGS.map((ring) => ring.textScale) },
        uRingColorMix: { value: TEXT_RINGS.map((ring) => ring.colorMix) },
        uRingTimeline: { value: TEXT_RINGS.map((ring) => ring.timelineInfluence) },
        // The gameplay ring reads strongly; the structure behind it less, so the
        // seal keeps a foreground and a background.
        uTimelineRing: { value: 0.88 },
        uTimelineDecor: { value: 0.62 },
        ...timelineUniforms(timeline),
        uRingMinRadius: { value: TEXT_RINGS.map((ring) => ring.minRadius) },
        uRingIsNote: { value: TEXT_RINGS.map((ring) => (ring.isNote ? 1 : 0)) },
        // Wide enough to hold the whole stack. The vertex stage builds exactly
        // this band, and `TEXT_RINGS` has to stay inside it — the test suite
        // checks that rather than trusting these two numbers to be updated
        // together.
        uDecorInner: { value: 0.90 },
        uDecorOuter: { value: 1.86 },
        uJudgePerfect: { value: judge.perfectSeconds },
        uJudgeGreat: { value: judge.greatSeconds },
        uJudgeGood: { value: judge.goodSeconds },
        uArcColorPerfect: { value: new Color(...ARC_COLORS.perfect) },
        uArcColorGreat: { value: new Color(...ARC_COLORS.great) },
        uArcColorGood: { value: new Color(...ARC_COLORS.good) },
        uArcColorFar: { value: new Color(...ARC_COLORS.far) },
        uArcColorLate: { value: new Color(...ARC_COLORS.late) },
        // The arc has to be findable in a frame already carrying six rings of
        // script, but it must not be the thing that pushes the frame over the
        // bloom threshold, which this phase may not touch.
        uArcGain: { value: 1.15 },
        // Shares the radial tick band, so the arc costs no extra geometry and
        // the ticks read as the dial it sweeps.
        uArcLo: { value: 0.90 },
        uArcHi: { value: 1.00 },
        uArcHalfWidth: { value: 0.22 },
        uArcPulse: { value: 0 },
        uArcReferenceAngle: { value: ARC_REFERENCE_ANGLE },
        uPerfectFlashColor: { value: new Color(...PERFECT_FLASH_COLOR) },
        uPerfectFlashGain: { value: 1.25 },
        // Far enough that the seal visibly leaves, close enough that it does
        // not sweep across its neighbours on the way out.
        uDissolveDrift: { value: 0.85 },
        uDissolveDustGain: { value: 0.55 },
        uBadGlitchColor: { value: new Color(...BAD_GLITCH_COLOR) },
        uGradeColor: { value: GRADE_COLORS.map((rgb) => new Color(...rgb)) },
        uGradeGain: { value: [...GRADE_GAINS] },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      fog: false,
    });

    this.coreMaterial = new ShaderMaterial({
      uniforms: {
        uHitRadius: { value: judge.noteHitRadius },
        uGain: { value: options.gain ?? 0.42 },
        uArcPulse: { value: 0 },
        uCorePullWindow: { value: judge.goodSeconds * 2.4 },
        // Wide enough that the closing is unmistakable, tight enough that the
        // quad it forces stays affordable at fifty simultaneous waves.
        uNoteStartMultiple: { value: 2.6 },
        uPerfectFlashColor: { value: new Color(...PERFECT_FLASH_COLOR) },
        uBadGlitchColor: { value: new Color(...BAD_GLITCH_COLOR) },
        uJudgeGood: { value: judge.goodSeconds },
        uTimelineNote: { value: 0.95 },
        // The fixed target's own settings. Separated from the note so the
        // thing the player aims at cannot be dimmed by a change meant for the
        // thing that moves.
        //
        // Set by measuring, not by taste. The note is a solid stroke at full
        // amplitude and the target a thin ring, so the numbers here look
        // lopsided against the note's 0.75 and are not — what matters is the
        // ratio on screen. At 2.6 the target measured *brighter* than the note
        // (184 against 150 in a bloom-free frame), which inverts the reading:
        // the fixed mark must sit under the moving one, because the note is
        // what the player tracks and the target is where it is going.
        uHitTargetGain: { value: 1.3 },
        uHitTargetWidth: { value: 0.022 },
        uHitTargetPixels: { value: 1.1 },
        // A third of the way, so the target still shifts with the timeline but
        // never disappears into the blue end of it.
        uHitTargetTimeline: { value: 0.34 },
        // Rises by half as the moment arrives. Enough to read as anticipation,
        // not so much that the fixed mark starts competing with the moving one
        // for attention before there is anything to do.
        uHitTargetPerfectLift: { value: 0.5 },
        ...timelineUniforms(timeline),
        uGradeColor: { value: GRADE_COLORS.map((rgb) => new Color(...rgb)) },
        uGradeGain: { value: [...GRADE_GAINS] },
      },
      vertexShader: CORE_VERTEX,
      fragmentShader: CORE_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      fog: false,
    });

    this.object = new Mesh(this.geometry, this.material);
    // Instances are positioned entirely in the shader, so the geometry's bounds
    // describe nothing meaningful.
    this.object.frustumCulled = false;
    this.object.renderOrder = 20;

    this.coreObject = new Mesh(this.coreGeometry, this.coreMaterial);
    this.coreObject.frustumCulled = false;
    // Above the seal: the thing the player has to hit should never be buried
    // under the thing they must not.
    this.coreObject.renderOrder = 21;
  }

  /** Radius of the drawn hit circle, in world units. */
  get hitRadius(): number {
    return this.coreMaterial.uniforms['uHitRadius']?.value as number;
  }

  get chartDriven(): boolean {
    return (this.material.uniforms['uChartDriven']?.value as number) > 0.5;
  }

  setChartDriven(value: boolean): void {
    const uniform = this.material.uniforms['uChartDriven'];
    if (uniform !== undefined) uniform.value = value ? 1 : 0;
    if (value) this.clearChartInstances();
  }

  /** In prototype mode the whole field is two uniform writes. */
  update(time: number, pulse: number): void {
    this.material.uniforms['uTime']!.value = time;
    this.material.uniforms['uPulse']!.value = pulse;
  }

  /**
   * The shared breath of every timing arc and centre.
   *
   * Driven from song time by the binder, never from frame time, so the pulse
   * stays with the music through a dropped frame or a restored tab.
   */
  setArcPulse(pulse: number): void {
    this.material.uniforms['uArcPulse']!.value = pulse;
    this.coreMaterial.uniforms['uArcPulse']!.value = pulse;
  }

  /**
   * Writes one chart-driven wave into an instance slot.
   *
   * Values are copied out; nothing here retains a reference to the source.
   */
  writeChartInstance(slot: number, data: ChartInstanceData): void {
    if (slot < 0 || slot >= this.capacity) return;
    const base = slot * 4;
    this.chartA[base] = data.centerX;
    this.chartA[base + 1] = data.centerY;
    this.chartA[base + 2] = data.radius;
    this.chartA[base + 3] = data.life;

    this.chartB[base] = data.gateAngle;
    this.chartB[base + 1] = data.gateHalfWidth;
    this.chartB[base + 2] = data.spinOuter;
    this.chartB[base + 3] = data.spinInner;

    this.chartC[base] = data.red;
    this.chartC[base + 1] = data.green;
    this.chartC[base + 2] = data.blue;
    this.chartC[base + 3] = data.pattern;

    this.chartD[base] = data.gateRed;
    this.chartD[base + 1] = data.gateGreen;
    this.chartD[base + 2] = data.gateBlue;
    this.chartD[base + 3] = data.state;

    this.chartE[base] = data.noteRed;
    this.chartE[base + 1] = data.noteGreen;
    this.chartE[base + 2] = data.noteBlue;
    this.chartE[base + 3] = data.noteWordIndex;

    this.chartF[base] = data.noteGain;
    this.chartF[base + 1] = data.notePulse;
    this.chartF[base + 2] = data.badFlash;
    this.chartF[base + 3] = data.visualLead;

    this.chartG[base] = data.spinRing0;
    this.chartG[base + 1] = data.spinRing1;
    this.chartG[base + 2] = data.spinRing2;
    this.chartG[base + 3] = data.spinRing4;

    this.chartH[base] = data.timingOffset;
    this.chartH[base + 1] = data.arcAngle;
    this.chartH[base + 2] = data.resolveProgress;
    this.chartH[base + 3] = data.gradeCode;
  }

  /** Word slots on the note ring — the range `noteWordIndex` is meaningful over. */
  get wordsPerRing(): number {
    return this.glyphAtlas.noteWords;
  }

  /** Per-wave inscription variants, the modulus for `ChartInstanceData.pattern`. */
  get patternCount(): number {
    return this.glyphAtlas.patternCount;
  }

  /** The inscribed ring table, for diagnostics and tests. */
  get textRings(): readonly TextRing[] {
    return TEXT_RINGS;
  }

  /** Hides every slot from `from` upward. */
  hideChartInstancesFrom(from: number): void {
    for (let slot = Math.max(0, from); slot < this.capacity; slot += 1) {
      this.chartA[slot * 4 + 3] = 0;
    }
  }

  /**
   * Limits drawing to the first `count` slots.
   *
   * Dead instances are collapsed to a degenerate point in the vertex shader, but
   * they are still submitted and still pay for vertex processing — with a 96-slot
   * pool and a dozen live waves that was most of the geometry cost in the frame.
   */
  setChartInstanceCount(count: number): void {
    const clamped = Math.max(0, Math.min(count, this.capacity));
    this.geometry.instanceCount = clamped;
    this.coreGeometry.instanceCount = clamped;
  }

  /** Uploads the chart instance buffers. One call per frame, after writing. */
  commitChartInstances(): void {
    for (const attribute of this.chartAttributes) attribute.needsUpdate = true;
  }

  private clearChartInstances(): void {
    for (let slot = 0; slot < this.capacity; slot += 1) this.chartA[slot * 4 + 3] = 0;
    this.commitChartInstances();
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.coreGeometry.dispose();
    this.coreMaterial.dispose();
    this.glyphAtlas.dispose();
  }
}

/** A unit quad for the centre discs; the shader scales it per instance. */
function buildQuad(): InstancedBufferGeometry {
  const corners = new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]);
  const geometry = new InstancedBufferGeometry();
  geometry.setAttribute('aCorner', new BufferAttribute(corners, 2));
  geometry.setIndex(new BufferAttribute(new Uint16Array([0, 1, 2, 0, 2, 3]), 1));
  return geometry;
}

/**
 * A closed triangle strip two vertices deep: `aAngle` walks the circle and
 * `aSide` selects the inner or outer edge. The vertex shader turns that into a
 * band of any radius and thickness, so one upload serves every ripple.
 */
function buildAnnulusStrip(segments: number): InstancedBufferGeometry {
  const vertexCount = (segments + 1) * 2;
  const angles = new Float32Array(vertexCount);
  const sides = new Float32Array(vertexCount);

  for (let segment = 0; segment <= segments; segment += 1) {
    const angle = (segment / segments) * Math.PI * 2;
    angles[segment * 2] = angle;
    sides[segment * 2] = 0;
    angles[segment * 2 + 1] = angle;
    sides[segment * 2 + 1] = 1;
  }

  const indices = new Uint16Array(segments * 6);
  for (let segment = 0; segment < segments; segment += 1) {
    const inner = segment * 2;
    const outer = inner + 1;
    const nextInner = inner + 2;
    const nextOuter = inner + 3;
    const base = segment * 6;
    indices[base] = inner;
    indices[base + 1] = outer;
    indices[base + 2] = nextInner;
    indices[base + 3] = outer;
    indices[base + 4] = nextOuter;
    indices[base + 5] = nextInner;
  }

  const geometry = new InstancedBufferGeometry();
  geometry.setAttribute('aAngle', new BufferAttribute(angles, 1));
  geometry.setAttribute('aSide', new BufferAttribute(sides, 1));
  geometry.setIndex(new BufferAttribute(indices, 1));
  return geometry;
}

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
